import { pxToRem } from '@fluentsms/agentnet-web-components';
import { FormControlLabel, Grid, InputAdornment, Switch, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  getLegalDescriptionTypeOptions,
  LegalDescriptionType,
} from 'features/files/fincen-reporting/property-information/constants';
import { PropertyAddress, PropertyAddressComplete } from 'features/files/fincen-reporting/types';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import MagicWandIcon from 'ui-kit/icons/MagicWand';
import { AgentNetDropdownSelector } from 'ui-kit/inputs';
import { EnhancedStateSelectField } from 'ui-kit/inputs/EnhancedStateSelectField';
import territories from 'ui-kit/inputs/geo-data/us-territory.json';
import GoogleAutocompleteSearch from 'ui-kit/inputs/GoogleAutocompleteSearch/GoogleAutocompleteSearch';
import { CountryCode, isUS } from '@agentnet/utils';
import states from 'ui-kit/inputs/StateSelectField/states.json';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import USPostalCodeField from 'ui-kit/inputs/USPostalCodeField/USPostalCodeField';
import { FieldValidationError } from 'utilities/validation/validation';

export interface PropertyAddressFormProps {
  address: PropertyAddressComplete | PropertyAddress;
  onChange: (updatedAddress: PropertyAddressComplete | PropertyAddress) => void;
  onValidationChange?: (isValid: boolean) => void;
  submitted?: boolean;
  index?: number;
  fileData?: any;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControlLabel: {
      marginLeft: 0,
      '& .MuiTypography-root': {
        fontSize: pxToRem(14),
        color: theme.palette.text.primary,
      },
    },
    switch: {
      marginRight: theme.spacing(1),
    },
    stateEndAdornment: {
      marginRight: theme.spacing(3),
    },
  }),
);

const PropertyAddressForm: FC<PropertyAddressFormProps> = ({
  address,
  onChange,
  onValidationChange,
  submitted = false,
  index = 0,
  fileData,
}) => {
  const classes = useStyles();
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [formSubmitted, setFormSubmitted] = useState(submitted);

  const addressComplete = address as PropertyAddressComplete;
  const cachedProperty = fileData?.properties?.[index || 0];

  const [autoFilledFields, setAutoFilledFields] = useState<string[]>(() => {
    const fields: string[] = [];
    if (cachedProperty && addressComplete) {
      if (addressComplete.address1 && addressComplete.address1 === cachedProperty.address1) {
        fields.push('address1');
      }
      if (addressComplete.address2 && addressComplete.address2 === cachedProperty.address2) {
        fields.push('address2');
      }
      if (addressComplete.city && addressComplete.city === cachedProperty.city) {
        fields.push('city');
      }
      if (addressComplete.state && addressComplete.state === (cachedProperty.state || cachedProperty.stateOrProvince)) {
        fields.push('state');
      }
      if (addressComplete.postalCode && addressComplete.postalCode === cachedProperty.postalCode) {
        fields.push('postalCode');
      }
    }
    return fields;
  });

  const [originalCachedValues, setOriginalCachedValues] = useState<{ [key: string]: string }>(() => {
    if (!cachedProperty) {
      return {
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
      };
    }
    return {
      address1: cachedProperty.address1 || '',
      address2: cachedProperty.address2 || '',
      city: cachedProperty.city || '',
      state: cachedProperty.state || '',
      postalCode: cachedProperty.postalCode || '',
    };
  });

  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { propertyAddressValidationErrors, clearPropertyAddressValidationErrors, generateButtonClicked } =
    fincenReportCtx ?? {};
  const [localNoStreetAddress, setLocalNoStreetAddress] = useState(
    (address as PropertyAddressComplete).noStreetAddress || false,
  );
  const [savedAddress1, setSavedAddress1] = useState('');
  const [savedAddress2, setSavedAddress2] = useState('');
  const [localLegalDescription, setLocalLegalDescription] = useState(addressComplete.legalDescription || '');
  const [isTyping, setIsTyping] = useState(false);

  const shouldShowIcon = (fieldName: string): boolean => {
    return autoFilledFields.includes(fieldName);
  };

  const renderMagicWandIcon = (fieldName: string) => {
    if (!shouldShowIcon(fieldName)) return undefined;
    return (
      <InputAdornment position="end" className={fieldName === 'state' ? classes.stateEndAdornment : undefined}>
        <Tooltip title="Auto-filled from file information" placement="top" arrow>
          <MagicWandIcon fontSize="small" />
        </Tooltip>
      </InputAdornment>
    );
  };

  const handleFieldValueChange = (fieldName: string, newValue: string) => {
    const cachedValue = originalCachedValues[fieldName] || '';
    const updatedAutoFilledFields = [...autoFilledFields];

    if (newValue === cachedValue && cachedValue) {
      if (!updatedAutoFilledFields.includes(fieldName)) {
        updatedAutoFilledFields.push(fieldName);
      }
    } else {
      const index = updatedAutoFilledFields.indexOf(fieldName);
      if (index > -1) {
        updatedAutoFilledFields.splice(index, 1);
      }
    }

    setAutoFilledFields(updatedAutoFilledFields);
  };

  const filteredPropertyErrors =
    propertyAddressValidationErrors && typeof index === 'number'
      ? propertyAddressValidationErrors.filter((error) => {
          return error.field.startsWith(`[${index}].`);
        })
      : [];

  const effectiveValidationErrors =
    filteredPropertyErrors && filteredPropertyErrors.length > 0 ? filteredPropertyErrors : validationErrors;

  const clearFieldError = (fieldName: string) => {
    if (
      !propertyAddressValidationErrors ||
      propertyAddressValidationErrors.length === 0 ||
      !fincenReportCtx?.setPropertyAddressValidationErrors ||
      typeof index !== 'number'
    ) {
      return;
    }

    const fieldToMatch = `[${index}].${fieldName}`;

    const filteredErrors = propertyAddressValidationErrors.filter((error) => {
      return error.field !== fieldToMatch;
    });

    if (filteredErrors.length !== propertyAddressValidationErrors.length) {
      clearPropertyAddressValidationErrors?.();
      if (filteredErrors.length > 0) {
        fincenReportCtx.setPropertyAddressValidationErrors(filteredErrors);
      }
    }
  };

  const handleFieldChange = (updatedAddress: PropertyAddressComplete | PropertyAddress) => {
    const changedFields = Object.keys(updatedAddress).filter(
      (key) => updatedAddress[key as keyof typeof updatedAddress] !== address[key as keyof typeof address],
    );

    if (
      propertyAddressValidationErrors &&
      propertyAddressValidationErrors.length > 0 &&
      fincenReportCtx?.setPropertyAddressValidationErrors &&
      typeof index === 'number'
    ) {
      let filteredErrors = propertyAddressValidationErrors;

      changedFields.forEach((field) => {
        const fieldToMatch = `[${index}].${field}`;
        filteredErrors = filteredErrors.filter((error) => error.field !== fieldToMatch);
      });

      if (changedFields.includes('usTerritory')) {
        const stateFieldToMatch = `[${index}].state`;
        filteredErrors = filteredErrors.filter((error) => error.field !== stateFieldToMatch);

        if (updatedAddress.usTerritory !== CountryCode.US) {
          updatedAddress = {
            ...updatedAddress,
            state: updatedAddress.usTerritory,
          };
        } else if (!changedFields.includes('state')) {
          updatedAddress = {
            ...updatedAddress,
            state: '',
          };
        }
      }

      if (filteredErrors.length !== propertyAddressValidationErrors.length) {
        clearPropertyAddressValidationErrors?.();
        if (filteredErrors.length > 0) {
          fincenReportCtx.setPropertyAddressValidationErrors(filteredErrors);
        }
      }
    } else if ('usTerritory' in updatedAddress && updatedAddress.usTerritory !== address.usTerritory) {
      if (updatedAddress.usTerritory !== CountryCode.US) {
        updatedAddress = {
          ...updatedAddress,
          state: updatedAddress.usTerritory,
        };
      } else if (!changedFields.includes('state')) {
        updatedAddress = {
          ...updatedAddress,
          state: '',
        };
      }
    }

    onChange(updatedAddress);
  };

  useEffect(() => {
    setFormSubmitted(submitted);
  }, [submitted]);

  // Initialize saved address values on mount
  useEffect(() => {
    if (!localNoStreetAddress) {
      setSavedAddress1(addressComplete.address1 || '');
      setSavedAddress2(addressComplete.address2 || '');
    }
  }, []);

  useEffect(() => {
    if (!addressComplete.legalDescriptionType) {
      onChange({
        ...addressComplete,
        legalDescriptionType: LegalDescriptionType.LOT_AND_BLOCK,
      });
    }
  }, []);

  // Sync local state when address prop changes externally (but not while user is typing)
  useEffect(() => {
    if (!isTyping) {
      setLocalLegalDescription(addressComplete.legalDescription || '');
    }
  }, [addressComplete.legalDescription, isTyping]);

  // Debounce legal description updates
  useEffect(() => {
    if (!isTyping) return;

    const timeoutId = setTimeout(() => {
      handleFieldChange({ ...addressComplete, legalDescription: localLegalDescription });
      setIsTyping(false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [localLegalDescription, isTyping, addressComplete, handleFieldChange]);

  const getFieldError = (fieldName: string): FieldValidationError | undefined => {
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;

    const fieldToMatch = `[${index}].${fieldName}`;
    return effectiveValidationErrors?.find((err) => err.field === fieldToMatch);
  };

  const getFieldName = (fieldName: string): string => {
    return typeof index === 'number' ? `[${index}].${fieldName}` : fieldName;
  };

  const stateOptions = states.map((state) => ({
    name: state.abbreviation,
    value: state.abbreviation,
  }));
  const territoryOptions = territories.map((territory) => ({
    name: territory.name,
    value: territory.abbreviation,
  }));
  const legalDescOptions = getLegalDescriptionTypeOptions();
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          className={classes.formControlLabel}
          control={
            <Switch
              checked={localNoStreetAddress}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const isChecked = e.target.checked;
                setLocalNoStreetAddress(isChecked);

                let updatedAddress;
                if (isChecked) {
                  // Save current values before clearing
                  setSavedAddress1(addressComplete.address1 || '');
                  setSavedAddress2(addressComplete.address2 || '');
                  updatedAddress = {
                    ...addressComplete,
                    noStreetAddress: true,
                    address1: '',
                    address2: '',
                  };
                } else {
                  // Restore saved values
                  updatedAddress = {
                    ...addressComplete,
                    noStreetAddress: false,
                    address1: savedAddress1,
                    address2: savedAddress2,
                  };
                }

                onChange(updatedAddress);
              }}
              className={classes.switch}
              color="primary"
              size="small"
              data-testid={`fincen-property-${index}-no-street-address-switch`}
            />
          }
          label="No Street Address"
          data-testid={`fincen-property-${index}-no-street-address-label`}
        />
      </Grid>

      {!localNoStreetAddress && (
        <>
          <Grid item xs={6} style={{ position: 'relative' }}>
            <GoogleAutocompleteSearch
              label="Address 1"
              required
              variant="outlined"
              fullWidth
              value={addressComplete.address1 || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                handleFieldValueChange('address1', newValue);
                handleFieldChange({ ...addressComplete, address1: newValue });
              }}
              data-testid={`fincen-property-${index}-address1-input`}
              name={getFieldName('address1')}
              errs={effectiveValidationErrors}
              showValidation={!!getFieldError('address1')}
              stateConstraint={
                isUS(addressComplete.usTerritory) && addressComplete.state ? addressComplete.state : undefined
              }
              onPlaceSelect={(place: any) => {
                const newUsTerritory = place?.country ?? addressComplete.usTerritory ?? '';
                const isNewUSCountry = isUS(newUsTerritory);

                const next = {
                  address1: place?.address1 ?? '',
                  address2: place?.address2 ?? addressComplete.address2 ?? '',
                  city: place?.city ?? '',
                  postalCode: place?.zipcode ?? '',
                  usTerritory: newUsTerritory,
                  state: isNewUSCountry ? place?.state ?? addressComplete.state ?? '' : newUsTerritory,
                };
                handleFieldValueChange('address1', next.address1);
                handleFieldChange({ ...addressComplete, ...next });
              }}
            />
            {shouldShowIcon('address1') && (
              <div style={{ position: 'absolute', right: '12px', top: '16px', pointerEvents: 'none', zIndex: 1 }}>
                <Tooltip title="Auto-filled from file information" placement="top" arrow>
                  <MagicWandIcon fontSize="small" />
                </Tooltip>
              </div>
            )}
          </Grid>

          <Grid item xs={6}>
            <AgentNetTextInput
              label="Address 2"
              variant="outlined"
              fullWidth
              value={addressComplete.address2 || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                handleFieldValueChange('address2', newValue);
                handleFieldChange({ ...addressComplete, address2: newValue });
              }}
              data-testid={`fincen-property-${index}-address2-input`}
              InputProps={{
                endAdornment: renderMagicWandIcon('address2'),
              }}
            />
          </Grid>
        </>
      )}

      <Grid item xs={3}>
        <AgentNetTextInput
          label="City"
          required
          variant="outlined"
          fullWidth
          value={addressComplete.city || ''}
          onChange={(e) => {
            const newValue = e.target.value;
            handleFieldValueChange('city', newValue);
            handleFieldChange({ ...addressComplete, city: newValue });
          }}
          data-testid={`fincen-property-${index}-city-input`}
          error={!!getFieldError('city')}
          helperText={getFieldError('city')?.message}
          InputProps={{
            endAdornment: renderMagicWandIcon('city'),
          }}
        />
      </Grid>
      <Grid item xs={3} style={{ position: 'relative' }}>
        {(() => {
          const isUSTerritory = addressComplete.usTerritory === CountryCode.US;
          return (
            <EnhancedStateSelectField
              label="State or U.S. Territory"
              required
              useStatesOnly={isUSTerritory}
              useTerritoriesOnly={!isUSTerritory}
              shouldDisplayAbbreviation={isUSTerritory}
              variant="outlined"
              fullWidth
              value={addressComplete.state || ''}
              onChange={(value: string) => {
                handleFieldValueChange('state', value);
                handleFieldChange({ ...addressComplete, state: value });
              }}
              dataTestId={`fincen-property-${index}-state-select`}
              error={!!getFieldError('state')}
              helperText={getFieldError('state')?.message}
              disabled={!isUSTerritory}
              name={getFieldName('state')}
            />
          );
        })()}
        {shouldShowIcon('state') && (
          <div style={{ position: 'absolute', right: '32px', top: '16px', pointerEvents: 'none' }}>
            <Tooltip title="Auto-filled from file information" placement="top" arrow>
              <MagicWandIcon fontSize="small" />
            </Tooltip>
          </div>
        )}
      </Grid>

      <Grid item xs={3}>
        <USPostalCodeField
          label="Zip"
          required
          variant="outlined"
          fullWidth
          value={addressComplete.postalCode || ''}
          onChange={(e) => {
            const newValue = e.target.value;
            handleFieldValueChange('postalCode', newValue);
            handleFieldChange({ ...addressComplete, postalCode: newValue });
          }}
          data-testid={`fincen-property-${index}-zip-input`}
          error={!!getFieldError('postalCode')}
          helperText={getFieldError('postalCode')?.message}
          InputProps={{
            endAdornment: renderMagicWandIcon('postalCode'),
          }}
        />
      </Grid>

      <Grid item xs={3}>
        <AgentNetDropdownSelector
          label="U.S or U.S. Territory"
          required
          options={territoryOptions}
          variant="outlined"
          fullWidth
          value={addressComplete.usTerritory || ''}
          menuOption={(value) => handleFieldChange({ ...addressComplete, usTerritory: value as string })}
          data-testid={`fincen-property-${index}-country-select`}
          showValidation={!!getFieldError('usTerritory')}
          defaultToFirst={false}
          error={!!getFieldError('usTerritory')}
          errs={effectiveValidationErrors}
          name={getFieldName('usTerritory')}
        />
      </Grid>
      <Grid item xs={6}>
        <AgentNetDropdownSelector
          label="Legal Description Type"
          required
          options={legalDescOptions}
          variant="outlined"
          fullWidth
          value={addressComplete.legalDescriptionType || ''}
          menuOption={(selectedValue) => {
            const selectedLegalDescriptionType = selectedValue as LegalDescriptionType;
            handleFieldChange({
              ...addressComplete,
              legalDescriptionType: selectedLegalDescriptionType,
              otherDescription:
                selectedLegalDescriptionType === LegalDescriptionType.OTHER ? addressComplete.otherDescription : '',
            });
          }}
          data-testid={`fincen-property-${index}-legal-desc-type-select`}
          showValidation={!!getFieldError('legalDescriptionType')}
          defaultToFirst={true}
          error={!!getFieldError('legalDescriptionType')}
          name={getFieldName('legalDescriptionType')}
          dropdowntype="outlined"
          errs={effectiveValidationErrors}
        />
      </Grid>

      {addressComplete.legalDescriptionType === LegalDescriptionType.OTHER && (
        <Grid item xs={6}>
          <AgentNetTextInput
            label="Other Description"
            required
            variant="outlined"
            fullWidth
            value={addressComplete.otherDescription || ''}
            onChange={(e) => handleFieldChange({ ...addressComplete, otherDescription: e.target.value })}
            data-testid={`fincen-property-${index}-other-description-input`}
            error={!!getFieldError('otherDescription')}
            helperText={getFieldError('otherDescription')?.message}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <AgentNetTextInput
          label="Legal Description"
          required
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={localLegalDescription}
          onChange={(e) => {
            setLocalLegalDescription(e.target.value);
            setIsTyping(true);
          }}
          data-testid={`fincen-property-${index}-legal-description-input`}
          error={!!getFieldError('legalDescription')}
          helperText={getFieldError('legalDescription')?.message}
        />
      </Grid>
    </Grid>
  );
};

export default PropertyAddressForm;
